// FeedScreen (TEMPLATES.md): a pinned header, a chronological feed with day
// dividers that stays anchored at the bottom, a "new messages" pill when
// you have scrolled up, and a pinned composer. Chat, trash talk, recaps.
// Lays out from its own box, so it works inside a drawer or a pane.
//
// THE COMPOSER TYPES EMOJI (PLAN-2026-10-27-more-fun-in-chat C1): a button
// beside the box opens the site's emoji, and `:` with two letters offers the
// ones whose names begin so; a whole `:fire:` becomes 🔥 as it is typed. An
// emoji goes where the cursor is.
//
// AND ANSWERS AND NAMES (C2): `@` offers the people the screen names, a pick
// putting `@Name ` in the box; and while the screen's reply is set, the box
// says whose line it answers, with a way to stop. Escape puts away what is
// open, the suggestions first, then the picker, then the reply.
//
// AND STICKERS (PLAN-2026-10-30-stickers §5): a second button beside the
// emoji's opens the screen's stickers in the picker's place, one or the
// other. A sticker picked is sent at once and the tray stays open.
//
// AND A PLAYER OR A GAME (PLAN-2026-10-31-players-and-games-in-chat §1): a
// third button opens the screen's way to pick one in the same place; the one
// picked is said over the box with a way to take it off, and the box sends
// with words or none while it is there.
//
// Clean (LOOKS.md Part 7): drawn through the kit, so a look restyles it.

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type SyntheticEvent,
} from 'react';
import { emojiNamed, searchEmoji } from '@fsp/domain';
import { deviceZone, useReaderZone } from '../data/hooks';
import { EmojiPicker, finePointer } from '../kit/EmojiPicker';
import { Button, IconButton, TextField } from '../kit/ui';

export interface FeedItem {
  id: string;
  at: string;
}

/** A day divider, in the reader's zone. */
function dayLabel(iso: string, zone: string): string {
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'long', timeZone: zone });
}

/** What the box offers for the word being typed: an emoji by its name, or a person. */
interface Suggestion {
  key: string;
  /** What the row shows. */
  shown: string;
  /** What replaces the word. */
  insert: string;
}

/** The suggestions at most, Slack's six. */
const SUGGESTIONS = 6;

/** `:fi` just before the cursor, at the start or after a space. */
const EMOJI_WORD = /(^|\s)(:[a-z0-9_+-]{2,})$/i;
/** `:fire:` just typed, whole, at the start or after a space. */
const EMOJI_DONE = /(^|\s):([a-z0-9_+-]{2,}):$/i;
/** `@` and the start of a name just before the cursor, at the start or after a space. */
const MENTION_WORD = /(^|\s)(@[^\s@]{0,30})$/u;

/** The people whose name, or a word of it, begins with what follows the `@`. */
function peopleFor(typed: string, names: readonly string[]): Suggestion[] {
  const t = typed.toLowerCase();
  return names
    .filter((n) =>
      n
        .toLowerCase()
        .split(/\s+/)
        .some((w) => w.startsWith(t)),
    )
    .slice(0, SUGGESTIONS)
    .map((n) => ({ key: n, shown: `@${n}`, insert: `@${n} ` }));
}

export function FeedScreen<T extends FeedItem>({
  header,
  notice,
  items,
  renderItem,
  composer,
  tray,
  onTrayAway,
  empty,
}: {
  header?: ReactNode;
  /** A line over the feed that is about the feed: it could not be reached, say. */
  notice?: ReactNode;
  items: T[];
  renderItem: (item: T) => ReactNode;
  composer?: {
    placeholder: string;
    /** Resolves true when it was sent, and the box empties; false keeps the words. */
    onSend: (body: string) => Promise<boolean> | boolean;
    maxLength?: number;
    disabled?: boolean;
    /** Trash-talk starters (CHAT.md): a tap puts one in the box; the sender edits and owns it. */
    starters?: string[];
    /** The emoji button and `:name:` (PLAN-2026-10-27-more-fun-in-chat C1). */
    emoji?: boolean;
    /** Whom `@` offers, by name (C2); none, no `@` suggestions. */
    mentions?: readonly string[];
    /** The line the next message answers (C2), said over the box until sent or stopped. */
    replying?: { id: string; author: string; body: string; onCancel: () => void } | null;
    /**
     * The sticker button (PLAN-2026-10-30-stickers): `draw` is the tray, given
     * what sends one and what puts it away; `onSend` resolves true when it
     * was sent.
     */
    stickers?: {
      onSend: (sticker: string) => Promise<boolean> | boolean;
      draw: (send: (sticker: string) => void, close: () => void) => ReactNode;
    };
    /**
     * A player or a game with the next line: `draw` is the tray that picks
     * one, given what puts it away; `attached` is the one going, said over
     * the box until it is sent, with what takes it off.
     */
    share?: {
      draw: (close: () => void) => ReactNode;
      attached: { label: string; onRemove: () => void } | null;
    };
  };
  /**
   * Something the feed's own screen opens over the composer — a message's
   * reaction picker — in place of the starters and the box's own picker.
   */
  tray?: ReactNode;
  /**
   * The box's emoji button was pressed while `tray` is open: put the tray
   * away, so the picker that opens is the box's and an emoji meant for the
   * box never becomes a reaction.
   */
  onTrayAway?: () => void;
  empty?: ReactNode;
}) {
  // The reader's days (B-181): a message sent late Sunday in Denver is Monday in Auckland.
  const zone = useReaderZone() ?? deviceZone();
  const list = useRef<HTMLDivElement>(null);
  const [pinned, setPinned] = useState(true);
  const [unseen, setUnseen] = useState(0);
  const lastNewest = useRef<string | null>(items[items.length - 1]?.id ?? null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  /** Where the cursor is in the box, as last seen. */
  const [caret, setCaret] = useState(0);
  /**
   * Where the next emoji goes when the box does not have the keyboard: a
   * phone's picker is tapped with the box blurred, and moving its cursor there
   * would bring the keyboard back over the picker.
   */
  const pendingCaret = useRef<number | null>(null);
  /**
   * Where the cursor goes once the box has drawn its new words, with the
   * keyboard: set as the box redraws, before the next key, so a fast typist's
   * next letter lands after what was put in and not before it.
   */
  const placeCaret = useRef<number | null>(null);
  useLayoutEffect(() => {
    const at = placeCaret.current;
    const el = input.current;
    if (at === null || !el) return;
    placeCaret.current = null;
    el.focus();
    el.setSelectionRange(at, at);
  });
  /** Which of the box's own panels is open over it: the emoji, the stickers or a share. */
  const [picking, setPicking] = useState<'emoji' | 'stickers' | 'share' | null>(null);
  const [active, setActive] = useState(0);
  /** The word the suggestions were put away for, with Escape. */
  const [dismissed, setDismissed] = useState<string | null>(null);
  const max = composer?.maxLength ?? 300;

  // A REPLY CHOSEN TAKES THE BOX: the next thing typed is the answer.
  const replyingId = composer?.replying?.id ?? null;
  useEffect(() => {
    if (replyingId !== null) input.current?.focus();
  }, [replyingId]);

  const scrollToBottom = (smooth = false) => {
    const el = list.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    setUnseen(0);
  };

  // Only on mount: land at the bottom.
  useEffect(() => {
    list.current?.scrollTo({ top: list.current.scrollHeight });
  }, []);

  // SOMETHING NEW AT THE BOTTOM, by the newest id rather than by the count: a
  // real league's feed is the newest page, a window that slides, so its length
  // stays put while messages arrive.
  const newestId = items[items.length - 1]?.id ?? null;
  useEffect(() => {
    const was = lastNewest.current;
    lastNewest.current = newestId;
    if (newestId === null || newestId === was) return;
    const from = was === null ? -1 : items.findIndex((i) => i.id === was);
    const added = from === -1 ? 1 : items.length - 1 - from;
    if (pinned) scrollToBottom(true);
    else setUnseen((n) => n + Math.max(added, 1));
    // `items` is read for the count and is current when the newest id moves.
  }, [newestId, pinned]);

  const onScroll = () => {
    const el = list.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setPinned(atBottom);
    if (atBottom) setUnseen(0);
  };

  const attached = composer?.share?.attached ?? null;
  const send = async () => {
    const body = draft.trim();
    if ((!body && attached === null) || !composer || sending) return;
    setSending(true);
    const sent = await composer.onSend(body);
    setSending(false);
    if (!sent) return;
    setDraft('');
    setCaret(0);
    pendingCaret.current = null;
    setPicking(null);
    setPinned(true);
    requestAnimationFrame(() => scrollToBottom(true));
  };

  /** A sticker, sent at once; the tray stays open for another. */
  const sendSticker = async (sticker: string) => {
    if (!composer?.stickers || sending) return;
    setSending(true);
    const sent = await composer.stickers.onSend(sticker);
    setSending(false);
    if (!sent) return;
    setPinned(true);
    requestAnimationFrame(() => scrollToBottom(true));
  };

  /** The box's words become `next`, the cursor at `at`. */
  const put = (next: string, at: number, focus: boolean) => {
    setDraft(next);
    setCaret(at);
    if (focus && input.current) {
      pendingCaret.current = null;
      placeCaret.current = at;
    } else pendingCaret.current = at;
  };

  /** An emoji from the picker, where the cursor is. */
  const insert = (text: string) => {
    const el = input.current;
    const start = pendingCaret.current ?? el?.selectionStart ?? draft.length;
    const end = pendingCaret.current ?? el?.selectionEnd ?? start;
    const next = draft.slice(0, start) + text + draft.slice(end);
    if (next.length > max) return;
    put(next, start + text.length, finePointer());
  };

  const typed = (value: string) => {
    const at = input.current?.selectionStart ?? value.length;
    pendingCaret.current = null;
    // `:fire:` TYPED WHOLE becomes 🔥 at once.
    const done = EMOJI_DONE.exec(value.slice(0, at));
    const known = done ? emojiNamed(done[2]!) : undefined;
    if (done && known) {
      const from = at - done[2]!.length - 2;
      put(value.slice(0, from) + known.char + value.slice(at), from + known.char.length, true);
      return;
    }
    setDraft(value);
    setCaret(at);
    setActive(0);
  };

  // NOTHING OFFERED UNDER A TRAY: the keys would take what nobody can see.
  const before = draft.slice(0, caret);
  const people = composer?.mentions ?? [];
  const mention = people.length > 0 && !tray ? MENTION_WORD.exec(before) : null;
  const word = mention ?? (composer?.emoji && !tray ? EMOJI_WORD.exec(before) : null);
  const wordKey = word ? `${caret}|${word[2]}` : null;
  const suggestions: Suggestion[] =
    !word || wordKey === dismissed
      ? []
      : mention
        ? peopleFor(word[2]!.slice(1), people)
        : searchEmoji(word[2]!, SUGGESTIONS).map((e) => ({
            key: e.char,
            shown: `${e.char} :${e.name}:`,
            insert: e.char,
          }));
  const choose = (s: Suggestion) => {
    if (!word) return;
    const from = caret - word[2]!.length;
    const next = draft.slice(0, from) + s.insert + draft.slice(caret);
    if (next.length > max) return;
    put(next, from + s.insert.length, true);
  };
  const keys = (e: KeyboardEvent<HTMLInputElement>) => {
    if (suggestions.length > 0) {
      const n = suggestions.length;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((a) => (a + (e.key === 'ArrowDown' ? 1 : n - 1)) % n);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        choose(suggestions[Math.min(active, n - 1)]!);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setDismissed(wordKey);
        return;
      }
    }
    if (e.key === 'Escape' && picking !== null) {
      e.stopPropagation();
      setPicking(null);
    } else if (e.key === 'Escape' && composer?.replying) {
      e.stopPropagation();
      composer.replying.onCancel();
    }
  };
  const moved = (e: SyntheticEvent<HTMLInputElement>) => {
    pendingCaret.current = null;
    setCaret(e.currentTarget.selectionStart ?? draft.length);
  };

  // What sits between the feed and the box, one at a time: the screen's own
  // tray, the suggestions for the word being typed, the picker, the starters.
  const over = tray ? (
    <div className="fsp-feed-tray">{tray}</div>
  ) : suggestions.length > 0 ? (
    <div className="fsp-feed-suggest" role="listbox" aria-label={mention ? 'People' : 'Emoji'}>
      {suggestions.map((s, i) => (
        <button
          key={s.key}
          type="button"
          role="option"
          aria-selected={i === Math.min(active, suggestions.length - 1)}
          className="fsp-feed-suggestion"
          // The box keeps the keyboard: a tap here is not a blur.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => choose(s)}
        >
          {s.shown}
        </button>
      ))}
    </div>
  ) : picking === 'emoji' ? (
    <div className="fsp-feed-tray">
      <EmojiPicker key="box" title="Emoji" onPick={insert} onClose={() => setPicking(null)} />
    </div>
  ) : picking === 'stickers' && composer?.stickers ? (
    <div className="fsp-feed-tray">
      {composer.stickers.draw(
        (sticker) => void sendSticker(sticker),
        () => setPicking(null),
      )}
    </div>
  ) : picking === 'share' && composer?.share ? (
    <div className="fsp-feed-tray">{composer.share.draw(() => setPicking(null))}</div>
  ) : composer?.starters?.length ? (
    <div className="fsp-feed-starters" aria-label="Starters">
      {composer.starters.map((s) => (
        <button
          key={s}
          type="button"
          className="fsp-feed-starter"
          onClick={() => {
            setDraft(s);
            setCaret(s.length);
            pendingCaret.current = null;
            setPinned(true);
          }}
        >
          {s}
        </button>
      ))}
    </div>
  ) : null;

  let lastDay = '';
  return (
    <div className="fsp-feed">
      {header ? <div className="fsp-feed-head">{header}</div> : null}
      {notice ? <div className="fsp-feed-notice">{notice}</div> : null}
      <div className="fsp-feed-body">
        <div className="fsp-feed-list" ref={list} onScroll={onScroll}>
          {items.length === 0 ? <div className="fsp-empty">{empty ?? 'Nothing yet.'}</div> : null}
          {items.map((item) => {
            const day = dayLabel(item.at, zone);
            const divider = day !== lastDay;
            lastDay = day;
            return (
              <div key={item.id}>
                {divider ? <div className="fsp-feed-day">{day}</div> : null}
                {renderItem(item)}
              </div>
            );
          })}
        </div>
        {unseen > 0 ? (
          <div className="fsp-feed-pill">
            <Button size="xs" variant="primary" icon="down" onClick={() => scrollToBottom(true)}>
              {`${unseen} new`}
            </Button>
          </div>
        ) : null}
      </div>
      {over}
      {composer?.replying ? (
        <div className="fsp-feed-replying">
          <span className="fsp-feed-replying-text">
            <span className="fsp-feed-replying-who">{`Replying to ${composer.replying.author}`}</span>
            <span className="fsp-feed-replying-body">{composer.replying.body}</span>
          </span>
          <IconButton
            icon="close"
            label="Stop replying"
            variant="quiet"
            size="sm"
            onClick={composer.replying.onCancel}
          />
        </div>
      ) : null}
      {attached !== null ? (
        <div className="fsp-feed-replying fsp-feed-attached">
          <span className="fsp-feed-replying-text">
            <span className="fsp-feed-replying-who">Sharing</span>
            <span className="fsp-feed-replying-body">{attached.label}</span>
          </span>
          <IconButton
            icon="close"
            label={`Take off ${attached.label}`}
            variant="quiet"
            size="sm"
            onClick={attached.onRemove}
          />
        </div>
      ) : null}
      {composer ? (
        <form
          className="fsp-feed-composer"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          {composer.emoji ? (
            <IconButton
              icon="emoji"
              label={picking === 'emoji' && !tray ? 'Close emoji' : 'Emoji'}
              variant="quiet"
              aria-expanded={picking === 'emoji' && !tray}
              disabled={composer.disabled}
              onClick={() => {
                if (tray) {
                  onTrayAway?.();
                  setPicking('emoji');
                } else setPicking((p) => (p === 'emoji' ? null : 'emoji'));
              }}
            />
          ) : null}
          {composer.stickers ? (
            <IconButton
              icon="sticker"
              label={picking === 'stickers' && !tray ? 'Close stickers' : 'Stickers'}
              variant="quiet"
              aria-expanded={picking === 'stickers' && !tray}
              disabled={composer.disabled}
              onClick={() => {
                if (tray) {
                  onTrayAway?.();
                  setPicking('stickers');
                } else setPicking((p) => (p === 'stickers' ? null : 'stickers'));
              }}
            />
          ) : null}
          {composer.share ? (
            <IconButton
              icon="add"
              label={
                picking === 'share' && !tray ? 'Close players and games' : 'Add a player or game'
              }
              variant="quiet"
              aria-expanded={picking === 'share' && !tray}
              disabled={composer.disabled}
              onClick={() => {
                if (tray) {
                  onTrayAway?.();
                  setPicking('share');
                } else setPicking((p) => (p === 'share' ? null : 'share'));
              }}
            />
          ) : null}
          <TextField
            label="Message"
            hideLabel
            grow
            placeholder={attached !== null ? 'Add words, or just send.' : composer.placeholder}
            value={draft}
            onChange={typed}
            maxLength={max}
            disabled={composer.disabled}
            inputRef={input}
            onKeyDown={keys}
            onSelect={moved}
          />
          <IconButton
            icon="send"
            label="Send"
            submit
            disabled={composer.disabled || sending || (!draft.trim() && attached === null)}
          />
        </form>
      ) : null}
    </div>
  );
}
